import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    AlertTriangle,
    BarChart3,
    Calendar,
    Check,
    DollarSign,
    Inbox,
    RefreshCw,
    Search,
    ShoppingCart,
    Star,
    Tag,
    TrendingUp,
    Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatCurrency } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import DialogForm from '../../component/dialog/DialogForm';
import { FilterChips } from '../../component/filter/FilterToolbar';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import DialogInfo from '../../component/dialog/DialogInfo';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import KpiTickerCard from '../../component/report/KpiTickerCard';
import DefaultTable from '../../component/table/DefaultTable';
import { usePermissions } from '../../hooks/usePermissions';
import '../reports/reports.css';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { parseMoney } from '../../component/common/MobileDashboardKit';
import { protoIcon } from '../../component/common/ProtoIcon';
import MobileReport, { MobileReportBody, formatMoneyKpi, formatMoneyRank, useMobileRegenerate } from '../../component/report/MobileReport';
import { Caption, ChartCard } from '../../component/report/MobileSalesSections';
import { BelowMarginList, MultiLine, ProfitabilityList, SeriesKey, SimpleRows } from '../../component/report/MobileProductSections';
import { KitBanner, KitChip, KitChips, KitField, KitInput } from '../../component/common/MobileKitExtras';
import { KitButton, KitPill } from '../../component/common/MobileKit';
import { HBars, KitEmptyNote } from '../../component/common/MobileDashboardKit';

/**
 * Cobertura de dados x protótipo (`prototipo-relatorio-produtos.html`):
 * `POST /chart/product` (`report/product.go`) cobre os KPIs (receita/custo/
 * líquido/margem, total vendido, estoque baixo, não vendidos), as duas
 * primeiras abas de gráficos (mais vendidos + receita x custo x líquido) e a
 * tabela de rentabilidade (por linha de venda/O.S., com custo editável e
 * `POST /chart/product/update-cost-sale-item` para persistir). A aba "Abaixo
 * da Margem" usa `POST /chart/product/below-margin` (produtos, não linhas de
 * venda) + `POST /chart/product/below-margin/apply` para o preço sugerido —
 * gates de permissão próprios (`report.product` e
 * `report.product.pricing`), preservados do comportamento
 * anterior desta tela.
 *
 * O que não fechou 1:1 com o protótipo, documentado em vez de simulado:
 * - Toolbar: sem os campos "Categoria" e "Margem mínima (%)" do protótipo —
 *   `/chart/product` não devolve categoria por produto na tabela de
 *   rentabilidade (só no CSV analítico) nem existe um conceito de "margem
 *   mínima" único e editável pelo usuário (a margem esperada é por
 *   categoria/subcategoria, calculada no servidor e usada só na aba "Abaixo
 *   da Margem"). Um select/campo decorativo que não filtra nada de verdade
 *   ficaria enganoso.
 * - KPI "Produtos ativos" e "Ganho potencial com preços sugeridos" do
 *   protótipo não têm campo equivalente em `ReportDataProduct` — substituídos
 *   pelos KPIs reais já devolvidos pelo endpoint (receita/custo/líquido/
 *   margem + total vendido/estoque baixo/não vendidos).
 * - Aba "Gráficos": os 2 primeiros painéis (mais vendidos, receita x custo x
 *   líquido) são reais; os 3 painéis extras do protótipo (vendas por
 *   categoria, dentro x fora da margem mínima, top 5 mais vendidos) exigiriam
 *   contagem de produtos ativos e vendas por categoria, que não existem
 *   nesse endpoint — "top 5 mais vendidos" já é coberto pelo gráfico de mais
 *   vendidos, então não foi duplicado como painel à parte.
 * - Aba "Rentabilidade": a "ação de aplicar preço sugerido" do protótipo não
 *   se aplica aqui — `Profitability.items` é por linha de venda/O.S. (`item_id`
 *   = id da linha), sem `product_id` nem preço sugerido; a ação real de
 *   aplicar preço sugerido (produto vivo, com `product_id` e margem esperada
 *   por categoria) só existe mesmo na aba "Abaixo da Margem".
 * - Aba "Relevantes": "produtos com baixo giro" (dias sem venda) do protótipo
 *   não tem campo equivalente — a API só devolve a lista de produtos nunca
 *   vendidos no período (`ProductsNotSold`), sem uma contagem de dias.
 */

const normalizeText = (s) =>
    String(s ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();

// Campos monetários já vêm formatados pelo backend (`money.Money.Format()`,
// ex. "1.234,56") sem o prefixo "R$" e com o sinal (quando houver) na frente
// da string — helpers abaixo só cuidam da apresentação, sem tocar no valor.
const formatBrl = (v) => `R$ ${v ?? '0,00'}`;
const formatSignedBrl = (v) => {
    const s = String(v ?? '0,00');
    return s.startsWith('-') ? `-R$ ${s.slice(1)}` : `R$ ${s}`;
};
const formatSignedFloatBrl = (n) => {
    const abs = Math.abs(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n < 0 ? `-R$ ${abs}` : `R$ ${abs}`;
};
// Converte uma string de dinheiro BR ("1.234,56") em número — usado só para
// a prévia ao vivo de lucro/margem enquanto o usuário edita o custo (a
// persistência real e os valores oficiais sempre vêm da resposta da API).
const parseBrMoney = (v) => {
    const s = String(v ?? '').trim();
    const negative = s.startsWith('-');
    const digitsAndComma = s.replace(/[^0-9,]/g, '');
    const n = parseFloat(digitsAndComma.replace(',', '.'));
    if (!Number.isFinite(n)) return 0;
    return negative ? -n : n;
};


const TB_FIELD_RADIUS = '9px';
const textFieldSx = {
    minWidth: 220,
    '& .MuiOutlinedInput-root': {
        height: 40,
        fontSize: '13px',
        borderRadius: TB_FIELD_RADIUS,
        backgroundColor: 'background.subtle',
    },
};

// DESIGN-SYSTEM.md §5.11 empty state (variante compacta) — mesmo componente
// local já usado nos outros relatórios.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// Painel de gráfico/card genérico (`.panel-head`) — mesmo componente local
// já usado nos outros relatórios.
function ChartPanel({ title, subtitle, headerActions, loading, children }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                        {loading ? (
                            <>
                                <Skeleton variant="text" width={160} height={20} />
                                <Skeleton variant="text" width={200} height={14} />
                            </>
                        ) : (
                            <>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                                {subtitle ? (
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>{subtitle}</Typography>
                                ) : null}
                            </>
                        )}
                    </Box>
                    {headerActions ? <Box sx={{ flexShrink: 0 }}>{headerActions}</Box> : null}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {children}
                </Box>
            </CardContent>
        </Card>
    );
}

// `.pill` — dot + rótulo, mesmo componente local de `CustomerReport.js`.
const PILL_TONE_SX = {
    green: { bgcolor: 'success.bg', color: 'success.main' },
    amber: { bgcolor: 'warning.bg', color: 'warning.main' },
    red: { bgcolor: 'error.bg', color: 'error.main' },
    blue: { bgcolor: 'info.bg', color: 'primary.main' },
    purple: { bgcolor: 'secondary.bg', color: 'secondary.main' },
    grey: { bgcolor: 'background.subtle', color: 'text.secondary' },
};
function Pill({ label, tone = 'grey' }) {
    const toneSx = PILL_TONE_SX[tone] || PILL_TONE_SX.grey;
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap', ...toneSx }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 }} />
            {label}
        </Box>
    );
}

// Origem da linha (venda x O.S.) — mesma convenção de cor do domínio
// `itemType` do `StatusChip` (Produto=info/azul, Serviço=purple), reaplicada
// aqui para distinguir as duas origens possíveis de uma linha de
// rentabilidade (DESIGN-SYSTEM.md §4 nota de reaproveitamento de tom, já
// usada por `personType` em `pages/customers/List.js`).
function TransactionOriginPill({ value }) {
    const isSale = String(value).toLowerCase() === 'sale';
    return <Pill label={isSale ? 'Venda' : 'O.S.'} tone={isSale ? 'blue' : 'purple'} />;
}

// Tabela compacta sem paginação/busca própria — mesmo padrão local de
// `CustomerReport.js` (`MiniTable`), usado aqui para as 3 listas da aba
// "Relevantes" (mais caros / estoque baixo / não vendidos), que a API
// devolve como arrays simples, sem paginação no protótipo.
const miniHeadCellSx = { py: 1.25, px: 1.5, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', borderBottom: '1px solid', borderColor: 'divider' };
const miniBodyCellSx = { py: 1.25, px: 1.5, fontSize: '12.5px', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider' };
function MiniTable({ columns, rows, renderRow, maxHeight = 320, emptyTitle, emptySubtitle }) {
    if (!rows || rows.length === 0) {
        return <EmptyMiniState icon={<Inbox size={22} strokeWidth={2} />} title={emptyTitle || 'Nenhum registro'} subtitle={emptySubtitle || 'Sem dados para exibir'} />;
    }
    return (
        <TableContainer sx={{ maxHeight, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell key={col.key} align={col.align || 'left'} sx={{ ...miniHeadCellSx, bgcolor: 'background.paper' }}>
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => renderRow(row, index))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const segTabButtonSx = {
    gap: '7px',
    padding: '9px 18px',
    lineHeight: 'normal',
    borderRadius: '8px !important',
    fontSize: '12.5px',
    fontWeight: 700,
};

const BELOW_MARGIN_TAB = 3;

export default function ProductReport() {
    const theme = useTheme();
    const { hasPermission } = usePermissions();

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // Tendência dos KPIs (`POST /chart/product/kpi-trend`); falha não derruba o relatório.
    const [kpiTrend, setKpiTrend] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [search, setSearch] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState('success');
    const [message, setMessage] = useState('');

    // ------- Rentabilidade: custo editável inline -------
    const [profitabilityRows, setProfitabilityRows] = useState([]);
    const [draftCosts, setDraftCosts] = useState({});
    const [savingItemId, setSavingItemId] = useState(null);
    const canEditCost = hasPermission('report.product.updatecost');

    // ------- Abaixo da margem -------
    const canBelowMargin = hasPermission('report.product');
    const canApplyBelowMargin = hasPermission('report.product.pricing');
    const [belowMarginItems, setBelowMarginItems] = useState([]);
    const [belowMarginLoading, setBelowMarginLoading] = useState(false);
    const belowMarginFetchGen = useRef(0);
    const [applyConfirmProduct, setApplyConfirmProduct] = useState(null);
    const [applyPriceLoading, setApplyPriceLoading] = useState(false);

    const formatMarginPct = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return '—';
        }
        return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    };

    const getErrorTextFromResponse = (response) => {
        const d = response?.data;
        if (typeof d === 'string' && d.trim()) return d.trim();
        return null;
    };

    const fetchBelowMargin = useCallback(async () => {
        if (!canBelowMargin) return;
        const gen = ++belowMarginFetchGen.current;
        setBelowMarginLoading(true);
        try {
            const response = await httpClient.post('/chart/product/below-margin', {});
            if (gen !== belowMarginFetchGen.current) return;
            if (response.status === 200 && Array.isArray(response.data?.items)) {
                setBelowMarginItems(response.data.items);
            } else {
                setDialogMessage(getErrorTextFromResponse(response) || 'Erro ao carregar produtos abaixo da margem.');
                setOpenDialog(true);
            }
        } catch (error) {
            if (gen !== belowMarginFetchGen.current) return;
            const msg = typeof error.response?.data === 'string' && error.response.data.trim()
                ? error.response.data.trim()
                : 'Erro ao carregar produtos abaixo da margem. Tente novamente.';
            setDialogMessage(msg);
            setOpenDialog(true);
        } finally {
            if (gen === belowMarginFetchGen.current) setBelowMarginLoading(false);
        }
    }, [canBelowMargin]);

    useEffect(() => {
        if (activeTab === BELOW_MARGIN_TAB && canBelowMargin) {
            fetchBelowMargin();
        }
    }, [activeTab, canBelowMargin, fetchBelowMargin]);

    const handleApplySuggestedPrice = async () => {
        const row = applyConfirmProduct;
        if (!row?.product_id) return;
        setApplyPriceLoading(true);
        try {
            const response = await httpClient.post('/chart/product/below-margin/apply', {
                product_id: row.product_id,
                confirm: true,
            });
            if (response.status === 204) {
                setApplyConfirmProduct(null);
                setSeverity('success');
                setMessage('Preço sugerido aplicado com sucesso.');
                setOpenSnackbar(true);
                await fetchBelowMargin();
                return;
            }
            setDialogMessage(getErrorTextFromResponse(response) || 'Não foi possível aplicar o preço sugerido.');
            setOpenDialog(true);
        } catch (error) {
            const msg = typeof error.response?.data === 'string' && error.response.data.trim()
                ? error.response.data.trim()
                : 'Erro ao aplicar o preço sugerido. Tente novamente.';
            setDialogMessage(msg);
            setOpenDialog(true);
        } finally {
            setApplyPriceLoading(false);
        }
    };

    // Filtros (mobile): volta aos valores padrão sem gerar o relatório.
    const handleClearFilters = () => {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs().endOf('month'));
        setSearch('');
    };

    const fetchReportData = async () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        setLoading(true);
        try {
            const period = {
                start: startDate.format('DD/MM/YYYY'),
                end: endDate.format('DD/MM/YYYY'),
            };
            const trendRequest = httpClient.post('/chart/product/kpi-trend', period)
                .then((res) => res.data)
                .catch(() => null);
            const response = await httpClient.post('/chart/product', period);
            setKpiTrend(await trendRequest);

            if (response.data) {
                setReportData(response.data);
                setProfitabilityRows(response.data.Profitability?.items || []);
                setDraftCosts({});
            }
        } catch (error) {
            setDialogMessage('Erro ao buscar dados do relatório. Tente novamente.');
            setOpenDialog(true);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (type) => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }
        try {
            const url = `/chart/product/export?start_date=${startDate.format('DD/MM/YYYY')}&end_date=${endDate.format('DD/MM/YYYY')}&type=${type}`;
            await httpClient.downloadFile(url, {
                title: `produtos_${startDate.format('DD/MM/YYYY')}_${endDate.format('DD/MM/YYYY')}`,
                extension: 'csv',
            });
        } catch (error) {
            setDialogMessage('Erro ao exportar relatório. Tente novamente.');
            setOpenDialog(true);
        }
    };

    const handleDraftCostChange = (itemId, rawValue) => {
        setDraftCosts((prev) => ({ ...prev, [itemId]: formatCurrency(rawValue) }));
    };

    const handleSaveCost = async (row) => {
        const draft = draftCosts[row.item_id];
        if (draft === undefined || draft === row.product_cost) return;
        setSavingItemId(row.item_id);
        try {
            const response = await httpClient.post('/chart/product/update-cost-sale-item', {
                item_id: row.item_id,
                cost_item: draft,
            });
            if (response.status !== 200) {
                setDialogMessage(typeof response.data === 'string' ? response.data : 'Erro ao atualizar custo.');
                setOpenDialog(true);
                return;
            }
            setProfitabilityRows((prev) => prev.map((r) => (
                r.item_id === row.item_id
                    ? { ...r, product_cost: draft, profit: response.data.profit, profitability: response.data.percentage }
                    : r
            )));
            setSeverity('success');
            setMessage('Custo atualizado com sucesso.');
            setOpenSnackbar(true);
        } catch (error) {
            const msg = typeof error.response?.data === 'string' && error.response.data.trim()
                ? error.response.data.trim()
                : 'Erro ao atualizar custo. Tente novamente.';
            setDialogMessage(msg);
            setOpenDialog(true);
        } finally {
            setSavingItemId(null);
        }
    };

    const filteredProfitabilityRows = useMemo(() => {
        const term = normalizeText(search);
        const rows = profitabilityRows.map((row) => ({ ...row, id: row.item_id }));
        if (!term) return rows;
        return rows.filter((row) => normalizeText(row.product_name).includes(term));
    }, [profitabilityRows, search]);

    const filteredBelowMarginItems = useMemo(() => {
        const term = normalizeText(search);
        const rows = belowMarginItems.map((row) => ({ ...row, id: row.product_id }));
        if (!term) return rows;
        return rows.filter((row) => normalizeText(row.product_name).includes(term));
    }, [belowMarginItems, search]);

    const topSellersData = useMemo(() => {
        if (!reportData?.TopSellers) return [];
        return reportData.TopSellers.Labels.map((label, index) => ({
            label,
            count: reportData.TopSellers.Values[index] || 0,
        }));
    }, [reportData]);

    const revenueLineData = useMemo(() => {
        if (!reportData?.ProductChartLineRevenue) return [];
        return reportData.ProductChartLineRevenue.Labels.map((label, index) => ({
            label,
            receita: Number(reportData.ProductChartLineRevenue.TotalRevenue[index]) || 0,
            custo: Number(reportData.ProductChartLineRevenue.TotalCost[index]) || 0,
            liquido: Number(reportData.ProductChartLineRevenue.TotalNet[index]) || 0,
        }));
    }, [reportData]);

    const isMobileShell = useIsMobileShell();
    // Mobile: gera ao abrir e a cada troca de período/filtros (mesma chamada e payload do botão "Gerar").
    const regenerate = useMobileRegenerate(fetchReportData, { enabled: isMobileShell });

    if (isMobileShell) {
        // Receita por produto = preço x unidades da rentabilidade (mesmo `Profitability.items` da tela).
        const revenueByName = {};
        const revenueRows = (profitabilityRows || []).map((row) => {
            const revenue = parseMoney(row.product_price) * (row.count_sold || 0);
            revenueByName[row.product_name] = (revenueByName[row.product_name] || 0) + revenue;
            return { name: row.product_name, revenue };
        });
        // Curva ABC: classe A até 70% da receita acumulada, B até 90%, C o restante.
        const totalRevenue = revenueRows.reduce((sum, row) => sum + row.revenue, 0);
        const classShare = { A: 0, B: 0, C: 0 };
        let acc = 0;
        [...revenueRows].sort((a, b) => b.revenue - a.revenue).forEach((row) => {
            const before = totalRevenue > 0 ? acc / totalRevenue : 0;
            const cls = before < 0.7 ? 'A' : before < 0.9 ? 'B' : 'C';
            classShare[cls] += row.revenue;
            acc += row.revenue;
        });
        const abc = totalRevenue > 0
            ? ['A', 'B', 'C'].map((cls) => ({ name: cls, value: classShare[cls], text: `${Math.round((classShare[cls] / totalRevenue) * 100)}%` }))
            : [];
        const download = protoIcon('download');

        // Totais e indicadores do desktop (mesmos campos de `/chart/product`).
        const signedKpi = (raw) => {
            const n = parseMoney(raw);
            return Math.abs(n) >= 100000 ? formatMoneyKpi(n) : formatSignedFloatBrl(n);
        };
        const totalKpis = [
            { label: 'Receita total', icon: 'dollar', value: signedKpi(reportData?.AmountRevenue ?? '0,00') },
            { label: 'Custo total', icon: 'tag', value: signedKpi(reportData?.AmountCost ?? '0,00') },
            { label: 'Total líquido', icon: 'trendup', value: signedKpi(reportData?.AmountNetRevenue ?? '0,00') },
            { label: 'Margem de lucro', icon: 'percent', value: `${reportData?.ProfitMargin || 0}%` },
        ];
        const generalKpis = [
            { label: 'Total vendido (unidades)', icon: 'cart', value: (reportData?.CountSold || 0).toLocaleString('pt-BR') },
            { label: 'Estoque baixo', icon: 'alert', value: (reportData?.CountProductsLowStock || 0).toLocaleString('pt-BR') },
            { label: 'Não vendidos', icon: 'box', value: (reportData?.CountProductNotSold || 0).toLocaleString('pt-BR') },
        ];

        // Rentabilidade: mesmo cálculo de lucro/margem (e prévia ao editar o custo) da tabela do desktop.
        const getProfitabilityRow = (row) => {
            const draft = draftCosts[row.item_id] ?? row.product_cost;
            const dirty = draft !== row.product_cost;
            const priceValue = parseBrMoney(row.product_price);
            const costValue = parseBrMoney(draft);
            const invalid = dirty && (draft === '' || costValue > priceValue);
            const preview = priceValue - costValue;
            const marginValue = Number(row.profitability) || 0;
            const previewPct = priceValue > 0 ? Math.round(((priceValue - costValue) / priceValue) * 100) : 0;
            return {
                name: row.product_name,
                isSale: String(row.type_transaction).toLowerCase() === 'sale',
                price: formatBrl(row.product_price),
                qty: String(row.count_sold ?? 0),
                cost: formatBrl(row.product_cost),
                costEditable: canEditCost,
                draft,
                dirty,
                invalid,
                saving: savingItemId === row.item_id,
                profit: dirty ? formatSignedFloatBrl(preview) : formatSignedBrl(row.profit),
                profitNegative: dirty ? preview < 0 : String(row.profit ?? '').startsWith('-'),
                profitPreview: dirty,
                margin: dirty ? `${previewPct}%` : formatMarginPct(marginValue),
                marginNegative: dirty ? previewPct < 0 : marginValue < 0,
                marginPreview: dirty,
            };
        };

        const topItems = topSellersData.map((item) => ({ name: item.label, value: Number(item.count) || 0, text: `${Number(item.count).toLocaleString('pt-BR')} un` }));
        const lineColors = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main];
        const lineItems = revenueLineData.map((r) => ({ label: r.label, series: [r.receita, r.custo, r.liquido] }));
        const noSales = <KitEmptyNote>Sem vendas finalizadas no período</KitEmptyNote>;
        const rentabilityHint = canEditCost
            ? 'Edite o campo Custo para recalcular o lucro e a margem em tempo real e toque no ✓ para salvar.'
            : 'Rentabilidade por linha de venda/O.S. no período selecionado.';

        const tabsDef = [
            { value: 0, label: 'Rentabilidade' },
            { value: 1, label: 'Gráficos' },
            { value: 2, label: 'Relevantes' },
            ...(canBelowMargin ? [{ value: BELOW_MARGIN_TAB, label: 'Abaixo da margem' }] : []),
        ];

        let tabContent = null;
        if (activeTab === 0) {
            tabContent = (
                <>
                    <Caption>{rentabilityHint}</Caption>
                    <Box sx={{ mt: '10px' }}>
                        <ProfitabilityList
                            key={`${filteredProfitabilityRows.length}-${search}`}
                            rows={filteredProfitabilityRows}
                            getRow={getProfitabilityRow}
                            onDraftChange={(row, value) => handleDraftCostChange(row.item_id, value)}
                            onSave={handleSaveCost}
                        />
                    </Box>
                </>
            );
        } else if (activeTab === 1) {
            tabContent = (
                <>
                    <ChartCard title="Mais vendidos" subtitle="Top 5 produtos por unidades vendidas no período">
                        {topItems.length > 0 ? <HBars items={topItems} /> : noSales}
                    </ChartCard>
                    <ChartCard title="Receita x Custo x Líquido" subtitle="Evolução mensal no período selecionado">
                        {lineItems.length > 0 ? (
                            <>
                                <MultiLine items={lineItems} colors={lineColors} />
                                <SeriesKey items={[{ label: 'Receita', color: lineColors[0] }, { label: 'Custo', color: lineColors[1] }, { label: 'Líquido', color: lineColors[2] }]} />
                            </>
                        ) : noSales}
                    </ChartCard>
                </>
            );
        } else if (activeTab === 2) {
            tabContent = (
                <>
                    <ChartCard title="Mais caros" subtitle="Top 7 por preço de venda" bare>
                        <SimpleRows
                            empty="Sem produtos cadastrados"
                            rows={(reportData?.ExpensiveProducts || []).map((row) => ({
                                title: row.Product,
                                end: <Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>{formatBrl(row.Price?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }))}</Box>,
                            }))}
                        />
                    </ChartCard>
                    <ChartCard title="Estoque baixo" subtitle="Quantidade disponível abaixo do mínimo" bare>
                        <SimpleRows
                            empty="Nenhum produto com estoque baixo"
                            rows={(reportData?.ProductsLowStock || []).map((row) => ({
                                title: row.ProductName,
                                end: row.ProductStock === 0 ? <KitPill tone="danger">Sem estoque</KitPill> : <KitPill tone="warning">{row.ProductStock} un.</KitPill>,
                            }))}
                        />
                    </ChartCard>
                    <ChartCard title="Não vendidos" subtitle="Sem nenhuma venda registrada" bare>
                        <SimpleRows
                            empty="Todos os produtos já venderam ao menos uma unidade"
                            rows={(reportData?.ProductsNotSold || []).map((name) => ({ title: name }))}
                        />
                    </ChartCard>
                </>
            );
        } else if (activeTab === BELOW_MARGIN_TAB && canBelowMargin) {
            tabContent = (
                <>
                    <KitBanner tone="danger" icon={protoIcon('alert')}>
                        <b>{belowMarginItems.length} produto(s)</b> estão com a margem atual abaixo da margem esperada (por categoria/subcategoria). Use <b>Aplicar preço sugerido</b> para atualizar o preço de venda ao valor necessário para atingi-la.
                    </KitBanner>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', m: '12px 0' }}>
                        <Box sx={{ flex: 1, minWidth: 0, fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>
                            Somente produtos ativos com preço de venda maior que zero e margem atual abaixo da margem esperada.
                        </Box>
                        <KitButton variant="soft" size="sm" sx={{ height: 44, minHeight: 44 }} disabled={belowMarginLoading} onClick={() => fetchBelowMargin()} startIcon={<RefreshCw size={14} strokeWidth={2} />}>
                            Atualizar
                        </KitButton>
                    </Box>
                    <BelowMarginList
                        key={`${filteredBelowMarginItems.length}-${search}`}
                        rows={filteredBelowMarginItems}
                        loading={belowMarginLoading}
                        canApply={canApplyBelowMargin}
                        onApply={setApplyConfirmProduct}
                        getRow={(row) => ({
                            name: row.product_name,
                            salePrice: formatBrl(row.sale_price),
                            suggested: formatBrl(row.suggested_sale_price),
                            current: formatMarginPct(row.current_profit_margin),
                            expected: formatMarginPct(row.expected_profit_margin),
                        })}
                    />
                </>
            );
        }

        const tabsNode = (
            <Box sx={{ mt: '22px' }}>
                <KitChips>
                    {tabsDef.map((tab) => (
                        <KitChip key={tab.value} active={activeTab === tab.value} onClick={() => setActiveTab(tab.value)}>{tab.label}</KitChip>
                    ))}
                </KitChips>
                <Box sx={{ mt: '10px' }}>{tabContent}</Box>
            </Box>
        );
        return (
            <PageScaffold
                title="Relatório de Produtos"
                titlePageTarget="Produtos"
                links={[{ title: 'Relatórios', link: '/reports' }]}
                severity={severity}
                message={message}
                setOpenSnackbar={setOpenSnackbar}
                openSnackbar={openSnackbar}
            >
                <MobileReport
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onGenerate={regenerate}
                    onClear={handleClearFilters}
                    exportItems={[
                        { label: 'Excel (CSV) · Analítico', icon: download, onClick: () => handleExport('analytical') },
                        { label: 'Excel (CSV) · Consolidado', icon: download, onClick: () => handleExport('consolidated') },
                    ]}
                    extraFilters={(
                        <KitField label="Buscar produto">
                            <KitInput type="search" aria-label="Buscar produto" placeholder="Nome do produto" value={search} onChange={(e) => setSearch(e.target.value)} />
                        </KitField>
                    )}
                >
                    <MobileReportBody
                        loading={loading}
                        ready={Boolean(reportData)}
                        kpis={[
                            { label: 'Vendidos', icon: 'package', value: `${(reportData?.CountSold || 0).toLocaleString('pt-BR')} un` },
                            { label: 'Receita', icon: 'dollar', grad: true, value: formatMoneyKpi(parseMoney(reportData?.AmountRevenue)) },
                            { label: 'Margem média', icon: 'percent', value: `${reportData?.ProfitMargin ?? 0}%` },
                            { label: 'Sem giro', icon: 'pause', value: (reportData?.CountProductNotSold || 0).toLocaleString('pt-BR'), delta: { text: '', tone: 'down' } },
                        ]}
                        sections={[
                            {
                                type: 'rank',
                                title: 'Mais vendidos',
                                rows: topSellersData.slice(0, 3).map((item) => ({
                                    title: item.label,
                                    sub: `${Number(item.count).toLocaleString('pt-BR')} un`,
                                    value: revenueByName[item.label] !== undefined ? formatMoneyRank(revenueByName[item.label]) : '—',
                                })),
                                empty: 'Nenhum produto vendido no período',
                            },
                            { type: 'hbars', title: 'Curva ABC', items: abc, empty: 'Sem receita no período' },
                            { type: 'kpis', title: 'Totais do período', items: totalKpis },
                            { type: 'kpis', title: 'Indicadores gerais de produtos', items: generalKpis },
                            { type: 'node', title: 'Detalhamento', node: tabsNode },
                        ]}
                    />
                </MobileReport>
                <DialogInfo open={openDialog} onClose={() => setOpenDialog(false)} title="Erro" iconType="error" content={dialogMessage} />
                <DialogForm
                    open={Boolean(applyConfirmProduct)}
                    iconType="warning"
                    title="Aplicar preço sugerido?"
                    subtitle={applyConfirmProduct?.product_name}
                    content={
                        applyConfirmProduct
                            ? 'O preço de venda será atualizado com o valor recalculado pelo servidor neste momento, com base no custo e na margem esperada vigentes. Confira os valores e confirme se deseja prosseguir.'
                            : ''
                    }
                    secondaryContent={
                        applyConfirmProduct
                            ? `Valor de venda atual: ${formatBrl(applyConfirmProduct.sale_price)} · Preço sugerido (referência na listagem): ${formatBrl(applyConfirmProduct.suggested_sale_price)} · Margem atual: ${formatMarginPct(applyConfirmProduct.current_profit_margin)} · Margem esperada: ${formatMarginPct(applyConfirmProduct.expected_profit_margin)}`
                            : ''
                    }
                    confirmButtonText="Confirmar"
                    cancelButtonText="Cancelar"
                    loading={applyPriceLoading}
                    loadingText="Aplicando…"
                    onClose={() => {
                        if (applyPriceLoading) return;
                        setApplyConfirmProduct(null);
                    }}
                    onSubmit={() => {
                        void handleApplySuggestedPrice();
                    }}
                />
            </PageScaffold>
        );
    }

    // Busca única por nome de produto (mesmo estado `search`), exibida acima das
    // tabelas de Rentabilidade e Abaixo da Margem.
    const productSearchField = (
        <TextField
            size="small"
            placeholder="Buscar produto pelo nome"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Buscar produto' }}
            sx={{ ...textFieldSx, width: { xs: '100%', sm: 320 }, mb: 2 }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Search size={15} strokeWidth={2} color={theme.palette.text.disabled} />
                    </InputAdornment>
                ),
            }}
        />
    );

    return (
        <PageScaffold
            title="Relatório de Produtos"
            titlePageTarget="Produtos"
            links={[{ title: 'Relatórios', link: '/reports' }]}
            severity={severity}
            message={message}
            setOpenSnackbar={setOpenSnackbar}
            openSnackbar={openSnackbar}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                    {/* page-head — ícone + título + descrição + ação primária (Exportar CSV). */}
                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                                <Tag size={18} strokeWidth={2} />
                            </Box>
                            <Box>
                                <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                    Relatório de Produtos
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                    Rentabilidade, produtos mais relevantes e itens abaixo da margem configurada.
                                </Typography>
                            </Box>
                        </Box>

                    </Box>

                    {/* filter-bar (sem card) — período + ações (Limpar / Exportar / Gerar). */}
                    <ReportFilterBar
                        start={startDate}
                        end={endDate}
                        onPeriodChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onGenerate={fetchReportData}
                        loading={loading}
                        onClear={handleClearFilters}
                        exportItems={[
                            { label: 'Analítico', description: 'Uma linha por venda/O.S. (CSV)', onClick: () => handleExport('analytical') },
                            { label: 'Consolidado', description: 'Totais por produto (CSV)', onClick: () => handleExport('consolidated') },
                        ]}
                    />

                    {/* Chips de contexto — período + totais reais devolvidos pela API. */}
                    <FilterChips sx={{ mb: { xs: 1.5, sm: 3 } }}>
                        <Chip
                            icon={<Calendar size={13} strokeWidth={2} />}
                            label={`Período: ${startDate?.format('DD/MM/YYYY')} – ${endDate?.format('DD/MM/YYYY')}`}
                            size="small"
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                        {reportData && (
                            <Chip
                                icon={<ShoppingCart size={13} strokeWidth={2} />}
                                label={`${(reportData.CountSold || 0).toLocaleString('pt-BR')} unidade(s) vendida(s) no período`}
                                size="small"
                                sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                            />
                        )}
                    </FilterChips>

                    {reportData ? (
                        <>
                            {/* KPIs estilo bolsa de valores — valor e variação vs. período
                                anterior (`KpiTickerCard`), sem a linha de tendência
                                (sparkline, `hideSpark`). */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 1, md: '10px' }, mb: '10px' }}>
                                <KpiTickerCard uid="rec" kind="money" hideSpark label="Receita" icon={<Wallet size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" data={kpiTrend?.revenue} days={kpiTrend?.days} value={Math.round(parseBrMoney(reportData.AmountRevenue) * 100)} loading={loading} />
                                <KpiTickerCard uid="cus" kind="money" hideSpark inverse label="Custo" icon={<Tag size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" data={kpiTrend?.cost} days={kpiTrend?.days} value={Math.round(parseBrMoney(reportData.AmountCost) * 100)} loading={loading} />
                                <KpiTickerCard uid="liq" kind="money" hideSpark label="Líquido (lucro)" icon={<TrendingUp size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" data={kpiTrend?.net} days={kpiTrend?.days} value={Math.round(parseBrMoney(reportData.AmountNetRevenue) * 100)} loading={loading} />
                                <KpiTickerCard uid="mar" kind="pct" hideSpark label="Margem" icon={<DollarSign size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" data={kpiTrend?.margin} days={kpiTrend?.days} value={Math.round(Number(reportData.ProfitMargin || 0) * 100)} loading={loading} />
                            </Box>

                            {/* seg-tabs — DESIGN-SYSTEM.md §5.4, mesmo `ToggleButtonGroup`
                                restilizado globalmente em `theme.js`, ordem igual à do
                                protótipo (Rentabilidade ativa por padrão). */}
                            <Box sx={{ mb: 2.25 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    value={activeTab}
                                    onChange={(e, v) => (v || v === 0) && setActiveTab(v)}
                                    sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px', flexWrap: 'wrap' }}
                                >
                                    <ToggleButton value={0} sx={segTabButtonSx}>
                                        <DollarSign size={14} strokeWidth={2} /> Rentabilidade
                                    </ToggleButton>
                                    <ToggleButton value={1} sx={segTabButtonSx}>
                                        <BarChart3 size={14} strokeWidth={2} /> Gráficos
                                    </ToggleButton>
                                    <ToggleButton value={2} sx={segTabButtonSx}>
                                        <Star size={14} strokeWidth={2} /> Relevantes
                                    </ToggleButton>
                                    {canBelowMargin && (
                                        <Tooltip title="Produtos com margem atual abaixo da margem esperada" placement="top" arrow>
                                            <ToggleButton value={BELOW_MARGIN_TAB} sx={segTabButtonSx}>
                                                <AlertTriangle size={14} strokeWidth={2} /> Abaixo da Margem
                                            </ToggleButton>
                                        </Tooltip>
                                    )}
                                </ToggleButtonGroup>
                            </Box>

                            {/* ===== TAB: RENTABILIDADE ===== */}
                            {activeTab === 0 && (
                                <Card>
                                    <CardContent sx={{ p: 2.25 }}>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mb: 2 }}>
                                            {canEditCost
                                                ? <>Edite o campo <b>Custo</b> para recalcular o lucro e a margem em tempo real e clique no ✓ para salvar.</>
                                                : 'Rentabilidade por linha de venda/O.S. no período selecionado.'}
                                        </Typography>
                                        {productSearchField}
                                        <DefaultTable
                                            data={filteredProfitabilityRows}
                                            colunms={[
                                                { path: 'product_name', name: 'Produto' },
                                                {
                                                    path: 'type_transaction',
                                                    name: 'Origem',
                                                    render: (row) => <TransactionOriginPill value={row.type_transaction} />,
                                                },
                                                {
                                                    path: 'product_price',
                                                    name: 'Preço de venda',
                                                    render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{formatBrl(row.product_price)}</Typography>,
                                                },
                                                {
                                                    path: 'product_cost',
                                                    name: 'Custo',
                                                    render: (row) => {
                                                        const draft = draftCosts[row.item_id] ?? row.product_cost;
                                                        const dirty = draft !== row.product_cost;
                                                        const priceValue = parseBrMoney(row.product_price);
                                                        const costValue = parseBrMoney(draft);
                                                        const invalid = dirty && (draft === '' || costValue > priceValue);
                                                        const saving = savingItemId === row.item_id;
                                                        if (!canEditCost) {
                                                            return <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{formatBrl(row.product_cost)}</Typography>;
                                                        }
                                                        return (
                                                            <Box sx={{ alignItems: 'center', gap: 0.5 }}>
                                                                <TextField
                                                                    size="small"
                                                                    value={draft}
                                                                    onChange={(e) => handleDraftCostChange(row.item_id, e.target.value)}
                                                                    error={invalid}
                                                                    disabled={saving}
                                                                    InputProps={{
                                                                        startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>R$</Typography></InputAdornment>,
                                                                    }}
                                                                    sx={{
                                                                        width: 110,
                                                                        '& .MuiOutlinedInput-root': { height: 34, fontSize: '12.5px', borderRadius: '8px', backgroundColor: 'background.subtle' },
                                                                    }}
                                                                />
                                                                {dirty && (
                                                                    <Tooltip title={invalid ? 'Custo não pode ser maior que o preço de venda' : 'Salvar novo custo'} arrow>
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleSaveCost(row)}
                                                                                disabled={invalid || saving}
                                                                                sx={{ width: 28, height: 28, border: '1px solid', borderColor: invalid ? 'error.main' : 'success.main', color: invalid ? 'error.main' : 'success.main' }}
                                                                            >
                                                                                {saving ? <CircularProgress size={14} /> : <Check size={14} strokeWidth={2.5} />}
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        );
                                                    },
                                                },
                                                {
                                                    path: 'profit',
                                                    name: 'Lucro',
                                                    render: (row) => {
                                                        const draft = draftCosts[row.item_id] ?? row.product_cost;
                                                        const dirty = draft !== row.product_cost;
                                                        if (!dirty) {
                                                            const negative = String(row.profit ?? '').startsWith('-');
                                                            return <Typography sx={{ fontSize: '13px', fontWeight: 700, color: negative ? 'error.main' : 'success.main' }}>{formatSignedBrl(row.profit)}</Typography>;
                                                        }
                                                        // Prévia ao vivo (cliente) enquanto o custo está sendo editado —
                                                        // mesma fórmula usada pelo backend (preço - custo); o valor
                                                        // oficial só é confirmado após salvar (resposta da API).
                                                        const preview = parseBrMoney(row.product_price) - parseBrMoney(draft);
                                                        return <Typography sx={{ fontSize: '13px', fontWeight: 700, fontStyle: 'italic', color: preview < 0 ? 'error.main' : 'success.main' }}>{formatSignedFloatBrl(preview)}</Typography>;
                                                    },
                                                },
                                                { path: 'count_sold', name: 'Qtd. vendida' },
                                                {
                                                    path: 'profitability',
                                                    name: 'Margem',
                                                    render: (row) => {
                                                        const draft = draftCosts[row.item_id] ?? row.product_cost;
                                                        const dirty = draft !== row.product_cost;
                                                        if (!dirty) {
                                                            const value = Number(row.profitability) || 0;
                                                            return <Pill label={formatMarginPct(value)} tone={value < 0 ? 'red' : 'green'} />;
                                                        }
                                                        const price = parseBrMoney(row.product_price);
                                                        const cost = parseBrMoney(draft);
                                                        const previewPct = price > 0 ? Math.round(((price - cost) / price) * 100) : 0;
                                                        return <Pill label={`${previewPct}% (prévia)`} tone={previewPct < 0 ? 'red' : 'green'} />;
                                                    },
                                                },
                                            ]}
                                            loading={loading}
                                            emptyMessage={search.trim() ? 'Nenhum produto encontrado para a busca informada.' : 'Nenhum produto vendido no período selecionado.'}
                                            iconsActions={[]}
                                            rowPerPage={10}
                                            itemLabel="linhas"
                                        />
                                    </CardContent>
                                </Card>
                            )}

                            {/* ===== TAB: GRÁFICOS ===== */}
                            {activeTab === 1 && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                    <ChartPanel title="Mais vendidos" subtitle="Top 5 produtos por unidades vendidas no período" loading={loading}>
                                        <Box sx={{ height: 280, width: '100%' }}>
                                            {loading ? (
                                                <Skeleton variant="rounded" width="100%" height={260} />
                                            ) : topSellersData.length > 0 ? (
                                                <BarChart
                                                    dataset={topSellersData}
                                                    height={260}
                                                    margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
                                                    borderRadius={6}
                                                    hideLegend
                                                    xAxis={[{
                                                        scaleType: 'band',
                                                        dataKey: 'label',
                                                        categoryGapRatio: 0.5,
                                                        disableLine: true,
                                                        disableTicks: true,
                                                        tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                                                    }]}
                                                    yAxis={[{ disableLine: true, disableTicks: true }]}
                                                    series={[{
                                                        dataKey: 'count',
                                                        color: theme.palette.primary.main,
                                                        valueFormatter: (value) => `${value} unidade(s)`,
                                                    }]}
                                                    sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                                                />
                                            ) : (
                                                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem vendas finalizadas no período" />
                                            )}
                                        </Box>
                                    </ChartPanel>
                                    <ChartPanel title="Receita x Custo x Líquido" subtitle="Evolução mensal no período selecionado" loading={loading}>
                                        <Box sx={{ height: 280, width: '100%' }}>
                                            {loading ? (
                                                <Skeleton variant="rounded" width="100%" height={260} />
                                            ) : revenueLineData.length > 0 ? (
                                                <LineChart
                                                    dataset={revenueLineData}
                                                    height={260}
                                                    margin={{ top: 16, right: 16, bottom: 24, left: 8 }}
                                                    grid={{ horizontal: true }}
                                                    xAxis={[{
                                                        scaleType: 'point',
                                                        dataKey: 'label',
                                                        disableLine: true,
                                                        disableTicks: true,
                                                        tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                                                    }]}
                                                    yAxis={[{ disableLine: true, disableTicks: true }]}
                                                    series={[
                                                        { dataKey: 'receita', label: 'Receita', color: theme.palette.primary.main },
                                                        { dataKey: 'custo', label: 'Custo', color: theme.palette.secondary.main },
                                                        { dataKey: 'liquido', label: 'Líquido', color: theme.palette.success.main },
                                                    ]}
                                                    sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                                                />
                                            ) : (
                                                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem vendas finalizadas no período" />
                                            )}
                                        </Box>
                                    </ChartPanel>
                                </Box>
                            )}

                            {/* ===== TAB: RELEVANTES ===== */}
                            {activeTab === 2 && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 2, alignItems: 'start' }}>
                                    <ChartPanel title="Mais caros" subtitle="Top 7 por preço de venda" loading={loading}>
                                        <MiniTable
                                            columns={[{ key: 'name', label: 'Produto' }, { key: 'price', label: 'Preço', align: 'right' }]}
                                            rows={reportData.ExpensiveProducts || []}
                                            emptyTitle="Nenhum produto"
                                            emptySubtitle="Sem produtos cadastrados"
                                            renderRow={(row, index) => (
                                                <TableRow key={`${row.Product}-${index}`} hover>
                                                    <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.Product}</TableCell>
                                                    <TableCell align="right" sx={miniBodyCellSx}>{formatBrl(row.Price?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }))}</TableCell>
                                                </TableRow>
                                            )}
                                        />
                                    </ChartPanel>
                                    <ChartPanel title="Estoque baixo" subtitle="Quantidade disponível abaixo do mínimo" loading={loading}>
                                        <MiniTable
                                            columns={[{ key: 'name', label: 'Produto' }, { key: 'stock', label: 'Estoque', align: 'right' }]}
                                            rows={reportData.ProductsLowStock || []}
                                            emptyTitle="Nenhum produto"
                                            emptySubtitle="Nenhum produto com estoque baixo"
                                            renderRow={(row, index) => (
                                                <TableRow key={`${row.ProductName}-${index}`} hover>
                                                    <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.ProductName}</TableCell>
                                                    <TableCell align="right" sx={miniBodyCellSx}>
                                                        {row.ProductStock === 0 ? <Pill label="Sem estoque" tone="red" /> : <Pill label={`${row.ProductStock} un.`} tone="amber" />}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        />
                                    </ChartPanel>
                                    <ChartPanel title="Não vendidos" subtitle="Sem nenhuma venda registrada" loading={loading}>
                                        <MiniTable
                                            columns={[{ key: 'name', label: 'Produto' }]}
                                            rows={reportData.ProductsNotSold || []}
                                            emptyTitle="Nenhum produto"
                                            emptySubtitle="Todos os produtos já venderam ao menos uma unidade"
                                            renderRow={(name, index) => (
                                                <TableRow key={`${name}-${index}`} hover>
                                                    <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{name}</TableCell>
                                                </TableRow>
                                            )}
                                        />
                                    </ChartPanel>
                                </Box>
                            )}

                            {/* ===== TAB: ABAIXO DA MARGEM ===== */}
                            {activeTab === BELOW_MARGIN_TAB && canBelowMargin && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: '13px 15px', borderRadius: '12px', bgcolor: 'error.bg', color: 'error.main', mb: 2, fontSize: '12.5px' }}>
                                        <AlertTriangle size={16} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                                        <span>
                                            <b>{belowMarginItems.length} produto(s)</b> estão com a margem atual abaixo da margem esperada (por categoria/subcategoria). Use <b>Aplicar preço sugerido</b> para atualizar o preço de venda ao valor necessário para atingi-la.
                                        </span>
                                    </Box>
                                    <Card>
                                        <CardContent sx={{ p: 2.25 }}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
                                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                                    Somente produtos ativos com preço de venda maior que zero e margem atual abaixo da margem esperada.
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={belowMarginLoading ? <CircularProgress size={14} /> : <RefreshCw size={14} strokeWidth={2} />}
                                                    onClick={() => fetchBelowMargin()}
                                                    disabled={belowMarginLoading}
                                                    sx={{ flexShrink: 0 }}
                                                >
                                                    Atualizar
                                                </Button>
                                            </Stack>
                                            {productSearchField}
                                            <DefaultTable
                                                data={filteredBelowMarginItems}
                                                colunms={[
                                                    { path: 'product_name', name: 'Produto' },
                                                    {
                                                        path: 'sale_price',
                                                        name: 'Valor de venda',
                                                        render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{formatBrl(row.sale_price)}</Typography>,
                                                    },
                                                    {
                                                        path: 'current_profit_margin',
                                                        name: 'Margem atual',
                                                        render: (row) => <Pill label={formatMarginPct(row.current_profit_margin)} tone="red" />,
                                                    },
                                                    {
                                                        path: 'expected_profit_margin',
                                                        name: 'Margem esperada',
                                                        render: (row) => <Pill label={formatMarginPct(row.expected_profit_margin)} tone="blue" />,
                                                    },
                                                    {
                                                        path: 'suggested_sale_price',
                                                        name: 'Preço sugerido',
                                                        render: (row) => <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{formatBrl(row.suggested_sale_price)}</Typography>,
                                                    },
                                                    {
                                                        path: 'product_id',
                                                        name: 'Ação',
                                                        render: (row) => (
                                                            canApplyBelowMargin ? (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => setApplyConfirmProduct(row)}
                                                                    sx={{ whiteSpace: 'nowrap' }}
                                                                >
                                                                    Aplicar preço sugerido
                                                                </Button>
                                                            ) : (
                                                                <Typography variant="caption" color="text.disabled">—</Typography>
                                                            )
                                                        ),
                                                    },
                                                ]}
                                                loading={belowMarginLoading}
                                                emptyMessage={search.trim() ? 'Nenhum produto encontrado para a busca informada.' : 'Nenhum produto abaixo da margem esperada no momento.'}
                                                iconsActions={[]}
                                                rowPerPage={10}
                                                itemLabel="produtos"
                                            />
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Card>
                            <CardContent sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 72, height: 72, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Tag size={30} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.secondary' }}>Nenhum relatório gerado</Typography>
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 380 }}>
                                    Selecione um período e clique em "Gerar relatório" para visualizar os dados de produtos.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </LocalizationProvider>

            <DialogInfo
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                title="Informação"
                iconType="info"
                content={dialogMessage}
            />

            <DialogForm
                open={Boolean(applyConfirmProduct)}
                iconType="warning"
                title="Aplicar preço sugerido?"
                subtitle={applyConfirmProduct?.product_name}
                content={
                    applyConfirmProduct
                        ? 'O preço de venda será atualizado com o valor recalculado pelo servidor neste momento, com base no custo e na margem esperada vigentes. Confira os valores e confirme se deseja prosseguir.'
                        : ''
                }
                secondaryContent={
                    applyConfirmProduct
                        ? `Valor de venda atual: ${formatBrl(applyConfirmProduct.sale_price)} · Preço sugerido (referência na listagem): ${formatBrl(applyConfirmProduct.suggested_sale_price)} · Margem atual: ${formatMarginPct(applyConfirmProduct.current_profit_margin)} · Margem esperada: ${formatMarginPct(applyConfirmProduct.expected_profit_margin)}`
                        : ''
                }
                confirmButtonText="Confirmar"
                cancelButtonText="Cancelar"
                loading={applyPriceLoading}
                loadingText="Aplicando…"
                onClose={() => {
                    if (applyPriceLoading) return;
                    setApplyConfirmProduct(null);
                }}
                onSubmit={() => {
                    void handleApplySuggestedPrice();
                }}
            />
        </PageScaffold>
    );
}
